import { ADJUSTMENTS, BLACK_TO_WHITE, type AdjustmentControl } from '../../adjustments';
import { linearCurves } from '../../adjustments/curves';
import { hexToRgba, rgbaToCss, rgbaToHex } from '../../core/color';
import { gradientToCss } from '../../core/gradient';
import type { AdjustmentState } from '../../core/types';
import { Checkbox, Select, SliderRow } from './controls';
import { CurveEditor } from './CurveEditor';

export interface AdjustmentEditorProps {
  state: AdjustmentState;
  onChange: (state: AdjustmentState) => void;
  /** Called when the gradient bar is clicked, for adjustments that own one. */
  onEditGradient?: () => void;
  histogram?: Float32Array | null;
}

/**
 * The body of an adjustment: its sliders, its switches and the editors for the
 * parts that are not sliders. The Adjustments dialog and the Properties panel of
 * an adjustment layer both render this, so the two always agree.
 */
export function AdjustmentEditor({
  state,
  onChange,
  onEditGradient,
  histogram,
}: AdjustmentEditorProps) {
  const definition = ADJUSTMENTS[state.id];
  const setValue = (key: string, value: number) =>
    onChange({ ...state, values: { ...state.values, [key]: value } });

  // A switch names the group of controls it reveals: only those are shown.
  const activeGroups = new Set<string>();
  for (const control of definition.controls) {
    if (control.kind !== 'switch') continue;
    const chosen = control.options.find((option) => option.value === state.values[control.key]);
    if (chosen?.group) activeGroups.add(chosen.group);
  }

  const visible = (control: AdjustmentControl): boolean =>
    control.group == null || activeGroups.has(control.group);

  return (
    <>
      {definition.extras?.includes('curves') && (
        <CurveEditor
          curves={state.curves ?? linearCurves()}
          histogram={histogram}
          onChange={(curves) => onChange({ ...state, curves })}
        />
      )}

      {definition.extras?.includes('gradient') && (
        <div className="field">
          <span className="field__label">Gradient</span>
          <button
            type="button"
            className="optionsbar__gradient"
            title="Edit the gradient"
            style={{
              backgroundImage: `linear-gradient(90deg, ${gradientToCss(state.gradient ?? BLACK_TO_WHITE)})`,
            }}
            onClick={onEditGradient}
          />
        </div>
      )}

      {definition.extras?.includes('color') && (
        <div className="field">
          <span className="field__label">Color</span>
          <label
            className="toolbar__swatch"
            style={{
              position: 'relative',
              width: 44,
              height: 22,
              background: rgbaToCss(state.color ?? { r: 1, g: 1, b: 1, a: 1 }),
            }}
          >
            <input
              type="color"
              style={{ opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
              value={rgbaToHex(state.color ?? { r: 1, g: 1, b: 1, a: 1 })}
              onChange={(event) => onChange({ ...state, color: hexToRgba(event.target.value) })}
            />
          </label>
        </div>
      )}

      {definition.controls.filter(visible).map((control) => {
        if (control.kind === 'slider') {
          return (
            <SliderRow
              key={control.key}
              label={control.label}
              value={state.values[control.key]}
              min={control.min}
              max={control.max}
              step={control.step}
              onChange={(value) => setValue(control.key, value)}
            />
          );
        }
        if (control.kind === 'toggle') {
          return (
            <Checkbox
              key={control.key}
              label={control.label}
              checked={state.values[control.key] > 0.5}
              onChange={(checked) => setValue(control.key, checked ? 1 : 0)}
            />
          );
        }
        return (
          <div className="field" key={control.key}>
            <span className="field__label">{control.label}</span>
            <Select
              value={String(state.values[control.key])}
              options={control.options.map((option) => ({
                value: String(option.value),
                label: option.label,
              }))}
              onChange={(value) => setValue(control.key, Number(value))}
            />
          </div>
        );
      })}
    </>
  );
}
